// Platform seam for microphone (RECORD_AUDIO) permission. Mirrors
// push/notificationPermission.ts: inside the Android WebView the getUserMedia
// prompt only shows when the activity is foregrounded and off the keyguard, so
// answering a call from the full-screen incoming ring silently denies a
// first-time request and the call fails with no dialog. We drive the grant
// natively instead — primed at onboarding, guarded again at call time.
//
// On the browser PWA there is no separate prompt: getUserMedia itself prompts
// at call time, and a call is always foregrounded, so there is nothing to do
// up front — the seam reports "granted" (no blocker) on web.
//
// isCapacitorNative is duplicated verbatim from app's shell/capacitor/platform.ts
// (own no-dependency probe of the WebView-injected `Capacitor` global) so the
// brain package never depends on shell/; the two are byte-identical and both
// deliberately avoid importing @capacitor/core. The native probe itself is
// INJECTED (`setNativeMicPermission`) rather than reached for via a dynamic
// import into frontend/app — the brain must never take a dependency on
// @capacitor/core or on the app package; see shell/capacitor/integrations.ts
// for where the app wires the real Capacitor-backed probe.

interface CapacitorGlobalLike {
  isNativePlatform?: () => boolean;
}

function isCapacitorNative(
  g: { Capacitor?: CapacitorGlobalLike } = globalThis as { Capacitor?: CapacitorGlobalLike },
): boolean {
  try {
    return g.Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

/**
 * Native mic-permission probe, injected by a host with real Capacitor access
 * (see shell/capacitor/integrations.ts). `request` drives the native
 * RECORD_AUDIO prompt; `status` checks the current grant without prompting.
 */
export interface NativeMicPermission {
  request(): Promise<boolean>;
  status(): Promise<boolean>;
}

let nativeMicPermission: NativeMicPermission | null = null;

/**
 * Inject (or clear, with `null`) the native mic-permission probe. Leaving it
 * unset keeps the pure-browser path below even inside a Capacitor WebView —
 * so the module's default, uninjected behavior is byte-for-byte the same as
 * before this seam existed.
 */
export function setNativeMicPermission(probe: NativeMicPermission | null): void {
  nativeMicPermission = probe;
}

/** Request microphone permission on whichever platform we're on. Never throws. */
export async function requestMicrophonePermission(): Promise<boolean> {
  try {
    if (isCapacitorNative() && nativeMicPermission) {
      return await nativeMicPermission.request();
    }
  } catch {
    // Permission prompts can reject on odd platforms — callers treat "not
    // granted" as skippable, never as an error.
  }
  // Web PWA: getUserMedia prompts at call time; nothing to pre-request.
  return !isCapacitorNative();
}

/** Current microphone permission state without prompting. Never throws. */
export async function hasMicrophonePermission(): Promise<boolean> {
  try {
    if (isCapacitorNative() && nativeMicPermission) {
      return await nativeMicPermission.status();
    }
  } catch {
    // fall through
  }
  return !isCapacitorNative();
}

export type MicPermissionState = 'granted' | 'denied' | 'prompt' | 'unknown';

/**
 * Best-effort read of the mic permission state, for UI MESSAGING ONLY — e.g.
 * telling the user a denial is permanent ('denied') vs. just needing another
 * attempt ('prompt'/'unknown'). Never use this to decide whether to attempt a
 * call: only an actual `getUserMedia()` call can trigger Chrome's own prompt,
 * so 'prompt' and 'unknown' (Permissions API unsupported, e.g. older WebViews
 * or Firefox, which never shipped the 'microphone' descriptor) must never
 * short-circuit the request path — the caller always proceeds to
 * getLocalAudio() regardless of what this resolves to. Never throws.
 */
export async function microphonePermissionState(): Promise<MicPermissionState> {
  try {
    if (isCapacitorNative()) {
      return (await hasMicrophonePermission()) ? 'granted' : 'denied';
    }
    const permissions = (navigator as { permissions?: Permissions }).permissions;
    const status = await permissions?.query({ name: 'microphone' as PermissionName });
    if (status?.state === 'granted' || status?.state === 'denied' || status?.state === 'prompt') {
      return status.state;
    }
  } catch {
    // Unsupported (e.g. Firefox has no 'microphone' PermissionName) or the
    // query itself rejected — treat exactly like 'prompt': unknown, not denied.
  }
  return 'unknown';
}
